import * as React from 'react';
import { ReactNode, memo, FunctionComponent, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo, NextSeoProps, OrganizationJsonLd } from 'next-seo';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { Footer, DefaultNavigationBar, NavigationVariantType, NavigationBarProps } from '@restworld/ui-components';
import { BASE_URL, cn, defaultSEO, getAssetSrc, withBrandSuffix } from '@restworld/utils-common';

import { buildLanguageLinks, LanguageSeoMode } from '../lib/seo/language-links';
import { mergeSeoProps } from '../lib/seo/merge-seo-props';
import { useLoginSignupPopup } from '../hooks';
import { AddressSearchBar } from '../components/job-explorer/address-search-bar';
import { jobApplicationWithPositiveSentimentAtom } from '../components/job-applications/components/worker-job-applications';
import useIsWorkerProfileComplete from '../components/profile/hooks/use-is-worker-profile-complete';
import WorkerEntryPointModal, {
  entryPointModalVisibleAtom,
} from '../components/worker-entry-point/worker-entry-point-modal';
import { activeTabAtom } from '../components/worker-entry-point/worker-entry-point.atoms';
import { isWorkerNavOverlayOpenAtom } from './wrapper-layout.atoms';

export const LayoutWrapper: FunctionComponent<ILayoutWrapperProps> = ({
  children,
  title,
  hasFooter,
  hasNavbar,
  navbarVariant,
  showSearchBarInNavbar = false,
  hasBorderBottom,
  noHorizontalPadding,
  noPaddingTop,
  noPaddingBottom,
  seoProps: customSeoProps,
  languageSeoMode,
  canonicalPath,
  testId,
  metaDescription,
  hideNavElement,
  additionalNavbarContentEl,
  additionalNavMenuContentEl,
  ...rest
}) => {
  const [isNavOverlay, navOverlayToggler] = useAtom(isWorkerNavOverlayOpenAtom);
  const loginSignupProps = useLoginSignupPopup();
  const jobApplicationWithPositiveSentiment = useAtomValue(jobApplicationWithPositiveSentimentAtom);
  // Read defensively: this layout wraps every page, so a throw here would break the whole screen.
  const workerActiveJobApplicationsCount = jobApplicationWithPositiveSentiment?.length ?? 0;
  const { isWorkerProfileComplete } = useIsWorkerProfileComplete();
  const setIsEntryPointModalVisible = useSetAtom(entryPointModalVisibleAtom);
  const setActiveTab = useSetAtom(activeTabAtom);

  const onShowUploadCvModal = React.useCallback(() => {
    setActiveTab('upload-cv');
    setIsEntryPointModalVisible(true);
  }, [setActiveTab, setIsEntryPointModalVisible]);

  const workerInfo = React.useMemo(
    () => ({
      activeApplicationsCount: workerActiveJobApplicationsCount,
      isProfileComplete: isWorkerProfileComplete,
    }),
    [isWorkerProfileComplete, workerActiveJobApplicationsCount]
  );

  const router = useRouter();
  // Merge over the defaults instead of replacing them: a partial custom object
  // (e.g. `seoProps={{ noindex: true }}`) must still inherit the default title,
  // description and Open Graph block.
  //
  // Through `mergeSeoProps` rather than a spread: see that file for why a spread
  // silently dropped `og:image` from every page that set an Open Graph block of
  // its own, and why a key a page set to `undefined` must not erase a default
  // either.
  const seoProps = mergeSeoProps(defaultSEO, customSeoProps);

  // The brand is applied exactly once, here. See withBrandSuffix for why it is
  // not delegated to next-seo's titleTemplate, and why an already branded title
  // (buildVenueMeta returns one) must be left alone.
  const pageTitle = withBrandSuffix(customSeoProps?.title || title);

  // One resolved description, falling back page prop -> custom seo -> default.
  // Trimmed at each step: a whitespace-only value is truthy, and one can arrive
  // from the backend (jobPositionStatic.meta_description is not cleaned the way
  // buildVenueMeta cleans its inputs), which would ship an empty description.
  const pageDescription = metaDescription?.trim() || customSeoProps?.description?.trim() || defaultSEO.description;

  // Strip canonical from seoProps to avoid duplicate <link rel="canonical"> tags (we manage it in <Head> below).
  // `titleTemplate` is stripped for the reason above: the title arrives already complete.
  const { canonical: _seoCanonical, titleTemplate: _seoTitleTemplate, ...seoPropsWithoutCanonical } = seoProps;
  const seoPropsFinal = ['production', 'development'].includes(process.env.NEXT_PUBLIC_APP_ENV)
    ? { ...seoPropsWithoutCanonical, title: pageTitle, description: pageDescription }
    : { ...seoPropsWithoutCanonical, title: pageTitle, description: pageDescription, noindex: true };

  // Canonical and hreflang together: which of the two versions gets indexed is
  // one decision, and splitting it is how they ended up contradicting each
  // other. `router.asPath` carries no locale prefix under Next.js i18n.
  const { canonical: canonicalUrl, alternates } = useMemo(
    () =>
      buildLanguageLinks({
        mode: languageSeoMode,
        path: router.asPath,
        canonicalPath,
        baseUrl: BASE_URL,
        locale: router.locale,
        defaultLocale: router.defaultLocale,
        locales: router.locales,
        canonical: seoProps?.canonical,
      }),
    [
      languageSeoMode,
      canonicalPath,
      router.asPath,
      router.locale,
      router.defaultLocale,
      router.locales,
      seoProps?.canonical,
    ]
  );

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        {/* Title and description are owned by <NextSeo> below, so they are emitted once. */}
        <link rel="shortcut icon" href={getAssetSrc('/utils-assets/favicons/favicon.ico')} />
        <link
          rel="icon"
          type="image/png"
          href={getAssetSrc('/utils-assets/favicons/favicon-96x96.png')}
          sizes="96x96"
        />
        <link rel="icon" type="image/svg+xml" href={getAssetSrc('/utils-assets/favicons/favicon.svg')} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={getAssetSrc('/utils-assets/favicons/apple-touch-icon.png')}
        />
        {/* Canonical. The key makes Next.js keep this one and drop the
            locale-blind default that `<CanonicalUrl />` mounts in `_app`. */}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        {/* Hreflang */}
        {alternates.map(({ hrefLang, href }) => (
          <link rel="alternate" hrefLang={hrefLang} href={href} key={`alternate-${hrefLang}`} />
        ))}
        {/* RSS Feed */}
        <link rel="alternate" type="application/rss+xml" title="Restworld - Offerte di lavoro" href="/feed.xml" />
      </Head>
      <NextSeo {...(seoPropsFinal ? seoPropsFinal : defaultSEO)} />
      <OrganizationJsonLd
        type="Organization"
        id="https://www.restworld.it"
        name="Restworld"
        url="https://www.restworld.it"
        logo="https://assets.restworld.it/images/restworld-seo-logo-2023.jpg"
        sameAs={[
          'https://www.linkedin.com/company/restworld/',
          'https://www.instagram.com/restworld.it/',
          'https://www.facebook.com/restworld.it/',
        ]}
      />
      {hasNavbar ? (
        <DefaultNavigationBar
          key="DefaultNavigationBar"
          variant={navbarVariant}
          authProps={{ ...loginSignupProps }}
          algoliaSearchBarElement={showSearchBarInNavbar ? <AddressSearchBar key="navbar" /> : null}
          hasBorderBottom={hasBorderBottom}
          testId={testId}
          hideNavElement={hideNavElement}
          additionalContentEl={additionalNavbarContentEl}
          workerInfo={workerInfo}
          onShowUploadCvModal={onShowUploadCvModal}
          isNavOverlay={isNavOverlay}
          navOverlayToggler={navOverlayToggler}
          additionalNavMenuContentEl={<WorkerEntryPointModal hideBackBtn />}
        />
      ) : null}
      <PaddingWrapper
        noHorizontalPadding={noHorizontalPadding}
        noPaddingTop={noPaddingTop}
        noPaddingBottom={noPaddingBottom}
        {...rest}
      >
        {children}
      </PaddingWrapper>
      {hasFooter ? <Footer /> : null}
    </>
  );
};

export default memo(LayoutWrapper);
interface ILayoutWrapperProps {
  title: string;
  children: ReactNode;
  hasFooter?: boolean;
  hasNavbar?: boolean;
  navbarVariant?: NavigationVariantType;
  showSearchBarInNavbar?: boolean;
  background?: NavigationBarProps['background'];
  hasBorderBottom?: boolean;
  noHorizontalPadding?: boolean;
  noPaddingTop?: boolean;
  noPaddingBottom?: boolean;
  seoProps?: NextSeoProps;
  /**
   * `consolidated` for pages whose main content is written by the client and
   * stays Italian in every locale: the English URL points its canonical at the
   * Italian one and no version declares hreflang alternates. Defaults to
   * `localized`, which is right for our own pages.
   */
  languageSeoMode?: LanguageSeoMode;
  /**
   * Path this page consolidates onto, when the URL served is not the one to
   * index. Needed by the category catch-all, which answers any depth.
   */
  canonicalPath?: string;
  testId?: string;
  metaDescription?: string;
  hideNavElement?: boolean;
  additionalNavbarContentEl?: ReactNode;
  additionalNavMenuContentEl?: ReactNode;
  className?: string;
}

export const standardHorizontalPadding = '2xl:px-[220px] xl:px-[120px] lg:px-12 px-6';
export const standardVerticalPadding = 'lg:pt-32 pt-32 lg:pb-16 pb-16';

export const PageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('min-h-screen bg-neutral-10', className)} {...props} />
);

export const PaddingWrapper = ({
  className,
  noHorizontalPadding,
  noPaddingTop,
  noPaddingBottom,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  noHorizontalPadding?: boolean;
  noPaddingTop?: boolean;
  noPaddingBottom?: boolean;
}) => (
  <div
    className={cn(
      'w-full',
      standardHorizontalPadding,
      standardVerticalPadding,
      noHorizontalPadding && '2xl:px-0 xl:px-0 lg:px-0 px-0',
      noPaddingTop && 'lg:pt-0 pt-0',
      noPaddingBottom && 'lg:pb-0 pb-0',
      className
    )}
    {...props}
  />
);
